import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FieldLabel } from '../FieldLabel'
import { CategoriesApi, KeywordsApi } from '@/services/targeting/targetingLookupApi'
import { formatPiped, type PipedValues } from '@/services/targeting/targeting'
import type { ChipTone } from '../ChipRow'
import { MatchRateBadge } from '../MatchRateBadge'
import { TokenInput, type TokenSuggestion } from '../TokenInput'

export type PipedTargetType = 'keyword' | 'category'

/**
 * What the network has already seen, offered as a list is typed into. The
 * badge is the share of ad requests the value is seen on, so a keyword or
 * category that would barely ever match can be told from one that matches
 * everything — the ad server reports no rate for some of them, and those are
 * offered without one.
 */
const rate = (frequency?: number) =>
  typeof frequency === 'number' && frequency > 0 ? <MatchRateBadge value={frequency} /> : undefined

const SUGGEST: Record<PipedTargetType, (term: string) => Promise<TokenSuggestion[]>> = {
  keyword: async (term) =>
    (await KeywordsApi.search(term)).results.map((row) => ({
      value: row.keyword,
      badge: rate(row.frequency),
    })),
  category: async (term) =>
    (await CategoriesApi.search(term)).results.map((row) => ({
      value: row.category,
      badge: rate(row.frequency),
    })),
}

export interface PipedTargetEditorProps {
  type: PipedTargetType
  value: PipedValues
  onChange: (value: PipedValues) => void
  /** The target says more than these three lists can — say so, and keep it. */
  advanced?: boolean
}

/**
 * Keyword and category targeting, which match a page's content profile. The
 * three lists are combined the way the ad server reads them: any one of the
 * first must match, every one of the second must, and none of the third may.
 */
export function PipedTargetEditor({ type, value, onChange, advanced }: PipedTargetEditorProps) {
  const { t } = useTranslation()

  /**
   * Which list a value was typed into is the editor's own to remember. The one
   * flat list the ad server stores cannot always say — `a|b` is plainly "match
   * any", but a single `a` reads as "must match" — so the lists are held here
   * and only re-read when the targeting changes into something this editor did
   * not write, such as the dialog being opened on another line item.
   */
  const [entries, setEntries] = useState<PipedValues>(value)
  const written = useRef(formatPiped(value).join('\u0000'))

  useEffect(() => {
    const incoming = formatPiped(value).join('\u0000')
    if (incoming === written.current) return
    written.current = incoming
    setEntries(value)
  }, [value])

  const update = (next: PipedValues) => {
    written.current = formatPiped(next).join('\u0000')
    setEntries(next)
    onChange(next)
  }

  // The tones are the summary's own, so a keyword is the same colour in the
  // editor as on the line item page: an alternative, a must-match, a ruled-out.
  const lists: { key: keyof PipedValues; label: string; tone: ChipTone }[] = [
    { key: 'any', label: t('targeting.matchAny'), tone: 'include' },
    { key: 'all', label: t('targeting.matchAll'), tone: 'required' },
    { key: 'none', label: t('targeting.matchNone'), tone: 'exclude' },
  ]

  return (
    <div className="flex flex-col gap-4">
      {advanced && (
        <p className="rounded-md border border-(--ad-badge-amber-border) bg-(--ad-badge-amber-bg) p-2 text-xs text-(--ad-badge-amber)">
          {t('targeting.advancedRows')}
        </p>
      )}
      <div className="grid gap-4 md:grid-cols-3">
        {lists.map((list) => (
          <div key={list.key} className="flex flex-col gap-1.5">
            <FieldLabel className="text-xs font-medium">{list.label}</FieldLabel>
            <TokenInput
              value={entries[list.key]}
              onChange={(values) => update({ ...entries, [list.key]: values })}
              placeholder={t(`targeting.types.${type}.placeholder`)}
              lowercase
              suggest={SUGGEST[type]}
              suggestQueryKey={`targeting-${type}-${list.key}-suggestions`}
              // These lists run long — a line item can name dozens — so the
              // field is a box rather than a line.
              tall
              tone={list.tone}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
